import React, { useState, useEffect } from 'react';
import { FormTemplate } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import { useOrganizations } from '../../hooks/useOrganizations';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import { 
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (templates: Partial<FormTemplate>[], organizationId: string, addSuffix?: boolean) => Promise<void>;
  importedTemplates: Partial<FormTemplate>[];
  isMultiple: boolean;
}

const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  importedTemplates,
  isMultiple
}) => {
  const { t } = useLanguage();
  const { organizations, loading: organizationsLoading } = useOrganizations();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [addImportedSuffix, setAddImportedSuffix] = useState(true);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen && organizations.length > 0) {
      setSelectedOrganizationId(organizations[0].id);
    }
  }, [isOpen, organizations]);

  const handleImport = async () => {
    if (!selectedOrganizationId) return;
    
    setIsImporting(true);
    try {
      await onImport(importedTemplates, selectedOrganizationId, addImportedSuffix);
      onClose();
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isMultiple ? 'Sablonok importálása' : 'Sablon importálása'}
      size="4xl"
    >
      <div className="space-y-6 p-6">
        {/* Modern Header Banner */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-lg"
        >
          <div className="flex items-center space-x-4">
            <motion.div 
              className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ArrowDownTrayIcon className="h-7 w-7 text-white" />
            </motion.div>
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-1">
                {isMultiple ? `${importedTemplates.length} sablon importálása` : 'Sablon importálása'}
              </h3>
              <p className="text-white/90 text-sm">
                Válaszd ki a célszervezetet és importáld a sablonokat
              </p>
            </div>
            <SparklesIcon className="h-8 w-8 text-white/60" />
          </div>
        </motion.div>

        {/* Import információ - Modern Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6 shadow-sm"
        >
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <InformationCircleIcon className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-4">
                Importálásra váró sablonok
              </h4>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                {isMultiple ? (
                  <div className="space-y-3">
                    <p className="font-semibold text-base">{importedTemplates.length} sablon:</p>
                    <div className="grid gap-2">
                      {importedTemplates.map((template, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + index * 0.05 }}
                          whileHover={{ scale: 1.02, x: 4 }}
                          className="flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-blue-100 dark:border-blue-800"
                        >
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <CheckCircleIcon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900 dark:text-gray-100 block truncate">
                              {(() => {
                                const originalName = template.name || `Névtelen sablon ${index + 1}`;
                                // Tisztítjuk az "(importált)" és "(importált 1)", "(importált 2)" stb. szövegeket
                                const cleanName = originalName.replace(/\s*\(importált\s*\d*\)\s*/g, '').trim();
                                if (addImportedSuffix) {
                                  return `${cleanName} (importált)`;
                                }
                                return cleanName;
                              })()}
                            </span>
                            {template.name && template.name.match(/\(importált\s*\d*\)/) && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Eredeti: {template.name}
                              </span>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <motion.div 
                    whileHover={{ scale: 1.01 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-blue-100 dark:border-blue-800"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <CheckCircleIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                          {addImportedSuffix ? 'Importálva mint:' : 'Sablon neve:'}
                        </span>
                        <span className="text-blue-900 dark:text-blue-100 font-bold text-base block">
                          {(() => {
                            const originalName = importedTemplates[0]?.name || 'Névtelen sablon';
                            // Tisztítjuk az "(importált)" és "(importált 1)", "(importált 2)" stb. szövegeket
                            const cleanName = originalName.replace(/\s*\(importált\s*\d*\)\s*/g, '').trim();
                            if (addImportedSuffix) {
                              return `${cleanName} (importált)`;
                            }
                            return cleanName;
                          })()}
                        </span>
                        {importedTemplates[0]?.name && importedTemplates[0].name.match(/\(importált\s*\d*\)/) && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                            Eredeti: {importedTemplates[0].name}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Szervezet választás - Modernized */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <label className="flex items-center space-x-3 text-lg font-bold text-gray-800 dark:text-gray-200">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <BuildingOffice2Icon className="h-5 w-5 text-white" />
            </div>
            <span>Célszervezet kiválasztása</span>
          </label>
          
          {organizationsLoading ? (
            <div className="animate-pulse bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 h-14 rounded-xl"></div>
          ) : organizations.length === 0 ? (
            <motion.div 
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-xl p-5 shadow-sm"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                </div>
                <span className="text-base font-semibold text-yellow-800 dark:text-yellow-200">
                  Nincs elérhető szervezet
                </span>
              </div>
            </motion.div>
          ) : (
            <div className="relative">
              <select
                value={selectedOrganizationId}
                onChange={(e) => setSelectedOrganizationId(e.target.value)}
                className="w-full px-5 py-4 pr-10 border-2 border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-white text-base font-medium transition-all hover:border-purple-400 bg-white"
                disabled={isImporting}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                <BuildingOffice2Icon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          )}
          
          <motion.div 
            whileHover={{ scale: 1.01 }}
            className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4"
          >
            <div className="flex items-start space-x-3">
              <span className="text-2xl">💡</span>
              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                A sablonok ebbe a szervezetbe lesznek importálva és azonnal elérhetők lesznek
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Név módosítás opció */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="space-y-3"
        >
          <motion.label 
            whileHover={{ x: 2 }}
            className="flex items-start space-x-4 cursor-pointer bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-5 transition-all hover:border-purple-400 dark:hover:border-purple-600"
          >
            <input
              type="checkbox"
              checked={addImportedSuffix}
              onChange={(e) => setAddImportedSuffix(e.target.checked)}
              className="mt-1 w-5 h-5 text-purple-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer"
            />
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                  "(importált)" utótag hozzáadása
                </span>
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold rounded-full">
                  Ajánlott
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {addImportedSuffix ? (
                  <>
                    A sablon nevekhez <span className="font-semibold text-purple-600 dark:text-purple-400">"(importált)"</span> utótag kerül, 
                    így könnyen megkülönböztethetők az eredeti sablonokról
                  </>
                ) : (
                  <>
                    A sablonok az <span className="font-semibold">eredeti nevükkel</span> lesznek importálva
                  </>
                )}
              </p>
            </div>
          </motion.label>
        </motion.div>

        {/* Figyelmeztetés - Modernized */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-2xl p-6 shadow-sm"
        >
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-lg text-amber-900 dark:text-amber-100 mb-4 flex items-center">
                <span className="mr-2">⚠️</span>
                Fontos tudnivalók
              </p>
              <div className="grid gap-3">
                {[
                  'Az importált sablonok új azonosítót kapnak',
                  ...(addImportedSuffix ? ['A sablon nevekhez "(importált)" utótag kerül'] : []),
                  'A jogosultságok alapértelmezett értékre állnak',
                  'Az eredeti létrehozási adatok nem kerülnek át'
                ].map((text, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.05 }}
                    whileHover={{ x: 4 }}
                    className="flex items-start space-x-3 bg-white/60 dark:bg-gray-800/60 rounded-lg p-3 backdrop-blur-sm"
                  >
                    <div className="w-6 h-6 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircleIcon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                      {text}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Akció gombok - Modernized */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-end space-x-4 pt-6 border-t-2 border-gray-200 dark:border-gray-700"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isImporting}
              className="px-8 py-3 text-base font-semibold rounded-xl border-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
            >
              Mégse
            </Button>
          </motion.div>
          <motion.div 
            whileHover={{ scale: 1.02 }} 
            whileTap={{ scale: 0.98 }}
          >
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={!selectedOrganizationId || organizations.length === 0 || isImporting}
              className="min-w-[180px] px-8 py-3 text-base font-bold rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl transition-all"
            >
              {isImporting ? (
                <div className="flex items-center justify-center">
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-3 border-white border-t-transparent rounded-full mr-3"
                  />
                  <span>Importálás...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                  <span>Importálás{isMultiple ? ` (${importedTemplates.length})` : ''}</span>
                </div>
              )}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </Modal>
  );
};

export default ImportModal;
